import React, { useState, useEffect } from 'react';
import { Box, TextField, Button, Typography, Paper, Alert, CircularProgress, InputAdornment } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import { fetchCommissionConfigFromAPI, updateCommissionConfigInAPI } from '../../../Redux/CommissionConfigDucks';

const CommissionConfigPage = () => {
    const dispatch = useDispatch();
    const config = useSelector(store => store.commissionConfig.config);

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [pct, setPct] = useState('');
    const [feedback, setFeedback] = useState(null); // { type, msg }

    useEffect(() => {
        (async () => {
            setLoading(true);
            await dispatch(fetchCommissionConfigFromAPI());
            setLoading(false);
        })();
    }, [dispatch]);

    useEffect(() => {
        if (config && config.defaultPct != null) {
            setPct(String(config.defaultPct));
        }
    }, [config]);

    const parsed = parseFloat(pct);
    const isValid = !isNaN(parsed) && parsed >= 0 && parsed <= 100;

    const handleSave = async () => {
        if (!isValid) return;
        setSaving(true);
        setFeedback(null);
        try {
            await dispatch(updateCommissionConfigInAPI(parsed));
            setFeedback({ type: 'success', msg: 'Comisión por defecto actualizada correctamente.' });
        } catch (error) {
            console.error('Error updating commission config:', error);
            setFeedback({ type: 'error', msg: 'No se pudo guardar. Verifica tus permisos (Super-Admin) e inténtalo de nuevo.' });
        } finally {
            setSaving(false);
        }
    };

    const formatDate = (iso) => {
        if (!iso) return '—';
        const d = new Date(iso);
        return isNaN(d.getTime()) ? '—' : d.toLocaleString('es-CL');
    };

    return (
        <div className="apple-container">
            <Paper className="apple-card" sx={{ maxWidth: 640, mx: 'auto', p: 4 }}>
                <Typography variant="h6" sx={{ fontWeight: 600, color: '#1D1D1F', mb: 1 }}>
                    Comisión por defecto
                </Typography>
                <Typography variant="body2" sx={{ color: '#86868B', mb: 3 }}>
                    Este porcentaje se aplica a todos los casos que no tengan una comisión específica.
                    El cambio puede tardar unos minutos en reflejarse en los servicios (cache).
                </Typography>

                {loading ? (
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress />
                    </Box>
                ) : (
                    <>
                        {feedback && (
                            <Alert severity={feedback.type} sx={{ mb: 2 }} onClose={() => setFeedback(null)}>
                                {feedback.msg}
                            </Alert>
                        )}

                        <TextField
                            label="Porcentaje de comisión"
                            type="number"
                            variant="outlined"
                            fullWidth
                            className="apple-input"
                            value={pct}
                            onChange={e => setPct(e.target.value)}
                            error={pct !== '' && !isValid}
                            helperText={pct !== '' && !isValid ? 'Debe ser un valor entre 0 y 100.' : ' '}
                            InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
                            inputProps={{ min: 0, max: 100, step: 0.5 }}
                        />

                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2 }}>
                            <Typography variant="caption" sx={{ color: '#86868B' }}>
                                Última actualización: {formatDate(config?.updatedAt)}
                                {config?.updatedBy ? ` · ${config.updatedBy}` : ''}
                            </Typography>
                            <Button
                                variant="contained"
                                className="apple-button"
                                onClick={handleSave}
                                disabled={!isValid || saving || (config && String(config.defaultPct) === pct)}
                            >
                                {saving ? 'Guardando…' : 'Guardar'}
                            </Button>
                        </Box>
                    </>
                )}
            </Paper>
        </div>
    );
};

export default CommissionConfigPage;
